import { useEffect, useState } from 'react'
import { supabase } from '@/lib/supabaseClient'
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
} from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { ConfirmDestructiveDialog } from '@/components/ui/confirm-destructive'
import { IconTip } from '@/components/ui/tooltip'
import { GripVertical, Edit, Trash2, Type, Link2, MessageSquare } from 'lucide-react'
import type { InterviewQuestion } from '@/hooks/useScorecardsConfiguration'
import { getScorecardTypeDef, SCORECARD_SMART_FIELD_TYPES } from '@/hooks/useScorecardsConfiguration'
import { cn } from '@/lib/utils'

interface InterviewQuestionsListProps {
  questions: InterviewQuestion[]
  onReorder: (questionIds: string[]) => void
  onEdit: (question: InterviewQuestion) => void
  onDelete: (questionId: string) => void
}

function SortableQuestionItem({
  question,
  onEdit,
  onDelete
}: {
  question: InterviewQuestion
  onEdit: (question: InterviewQuestion) => void
  onDelete: (questionId: string) => void
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: question.id })

  const style = {
    transform: CSS.Translate.toString(transform),
    transition,
    opacity: isDragging ? 0 : undefined,
  }

  const typeDef = getScorecardTypeDef(question.answer_type)
  const TypeIcon = typeDef.icon
  const isSmart = SCORECARD_SMART_FIELD_TYPES.has(question.answer_type)
  const isSalaryType = question.answer_type === 'salary_expectations'

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        'flex items-start gap-3 p-4 bg-white border border-virgilio-border rounded-lg',
        'hover:border-virgilio-purple/50 transition-all duration-200'
      )}
    >
      <button
        className="cursor-grab active:cursor-grabbing text-virgilio-muted hover:text-virgilio-text mt-1"
        {...attributes}
        {...listeners}
      >
        <GripVertical className="h-5 w-5" />
      </button>

      <div className="flex-1 min-w-0">
        <p className="font-medium text-virgilio-text">{question.question_text}</p>
        
        <div className="flex flex-wrap items-center gap-2 mt-2">
          <Badge variant="outline" className={cn(
            "text-xs border-virgilio-purple/30",
            isSalaryType ? "bg-green-500/10 text-green-700 border-green-300" :
            isSmart ? "bg-[#EDE4FF] text-virgilio-purple border-[#D7C5FB]" :
            "bg-virgilio-purple/10 text-virgilio-purple"
          )}>
            <TypeIcon className="h-4 w-4" />
            <span className="ml-1">{typeDef.label}</span>
            {isSmart && !isSalaryType && (
              <span className="ml-1.5 text-[10px] uppercase tracking-wide opacity-70">Smart</span>
            )}
          </Badge>
          
          {question.is_required && (
            <Badge variant="outline" className="text-xs bg-amber-500/10 text-amber-700 border-amber-300">
              Required
            </Badge>
          )}
          
          {isSalaryType && (
            <>
              <Badge variant="outline" className="text-xs bg-blue-500/10 text-blue-700 border-blue-300 gap-1">
                <Link2 className="h-3 w-3" />
                Syncs to Profile
              </Badge>
              {question.salary_config && (
                <Badge variant="outline" className="text-xs bg-gray-100 text-gray-600">
                  {question.salary_config.currency} / {question.salary_config.period}
                </Badge>
              )}
            </>
          )}
        </div>

        {question.notes_for_interviewer && (
          <div className="mt-2 flex items-start gap-1.5 text-xs text-virgilio-muted">
            <MessageSquare className="h-3 w-3 mt-0.5 shrink-0" />
            <span className="line-clamp-2">{question.notes_for_interviewer}</span>
          </div>
        )}
      </div>

      <div className="flex gap-2 shrink-0">
        <IconTip label="Edit question">
          <Button
            variant="ghost"
            size="icon"
            aria-label="Edit question"
            onClick={() => onEdit(question)}
            className="hover:bg-virgilio-purple/10 hover:text-virgilio-purple"
          >
            <Edit className="h-4 w-4" />
          </Button>
        </IconTip>
        <IconTip label="Delete question">
          <Button
            variant="ghost"
            size="icon"
            aria-label="Delete question"
            onClick={() => onDelete(question.id)}
            className="hover:bg-destructive/10 hover:text-destructive"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </IconTip>
      </div>
    </div>
  )
}

export function InterviewQuestionsList({
  questions,
  onReorder,
  onEdit,
  onDelete
}: InterviewQuestionsListProps) {
  const [items, setItems] = useState(questions)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [questionToDelete, setQuestionToDelete] = useState<string | null>(null)
  // Answers go with the question (the database deletes them), so say how many.
  const [answerCount, setAnswerCount] = useState<number | null>(null)
  useEffect(() => {
    if (!questionToDelete) { setAnswerCount(null); return }
    let alive = true
    supabase
      .from('scorecard_question_responses')
      .select('id', { count: 'exact', head: true })
      .eq('question_id', questionToDelete)
      .then(({ count }) => { if (alive) setAnswerCount(count ?? 0) })
    return () => { alive = false }
  }, [questionToDelete])
  const [activeId, setActiveId] = useState<string | null>(null)

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 180, tolerance: 8 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  )

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(String(event.active.id))
  }

  const handleDragEnd = (event: DragEndEvent) => {
    setActiveId(null)
    const { active, over } = event

    if (over && active.id !== over.id) {
      const oldIndex = items.findIndex((item) => item.id === active.id)
      const newIndex = items.findIndex((item) => item.id === over.id)

      const newItems = arrayMove(items, oldIndex, newIndex)
      setItems(newItems)
      onReorder(newItems.map(item => item.id))
    }
  }

  const handleDeleteClick = (questionId: string) => {
    setQuestionToDelete(questionId)
    setDeleteDialogOpen(true)
  }

  const handleConfirmDelete = () => {
    if (questionToDelete) onDelete(questionToDelete)
  }
  // Kept while the dialog closes so its exit still shows the question.
  const pendingQuestion = items.find((q) => q.id === questionToDelete)?.question_text?.trim()

  // Sync items with questions prop when it changes
  if (questions !== items) {
    setItems(questions)
  }

  if (questions.length === 0) {
    return (
      <div className="text-center py-12 px-4 border-2 border-dashed border-virgilio-border rounded-lg bg-surface-secondary">
        <Type className="h-12 w-12 mx-auto text-virgilio-muted mb-3" />
        <h3 className="text-lg font-semibold text-virgilio-text mb-2">No Interview Questions Yet</h3>
        <p className="text-sm text-virgilio-muted mb-4 max-w-md mx-auto">
          Start building your scorecard by adding interview questions. Questions will be displayed to interviewers when they submit their scorecards.
        </p>
      </div>
    )
  }

  return (
    <>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={items.map(item => item.id)}
          strategy={verticalListSortingStrategy}
        >
          <div className="space-y-2">
            {items.map((question) => (
              <SortableQuestionItem
                key={question.id}
                question={question}
                onEdit={onEdit}
                onDelete={handleDeleteClick}
              />
            ))}
          </div>
        </SortableContext>
        <DragOverlay dropAnimation={{ duration: 200, easing: 'ease' }}>
          {activeId && (() => {
            const question = items.find(q => q.id === activeId)
            if (!question) return null
            return (
              <div style={{ transform: 'rotate(-1.5deg) scale(1.03)', boxShadow: '0 12px 24px rgba(0,0,0,0.15)' }}>
                <SortableQuestionItem
                  question={question}
                  onEdit={() => {}}
                  onDelete={() => {}}
                />
              </div>
            )
          })()}
        </DragOverlay>
      </DndContext>

      {/* J19 (Block B §16, Pattern 1): answers go with the question (the
          database cascades them), so the dialog says how many. */}
      <ConfirmDestructiveDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        title={pendingQuestion ? `Delete “${pendingQuestion}”?` : 'Delete this question?'}
        description={
          answerCount === null
            ? 'Checking for answers…'
            : answerCount === 0
              ? 'No scorecard has answered it yet. It stops appearing on new scorecards for this stage. This can’t be undone.'
              : `The ${answerCount} answer${answerCount === 1 ? '' : 's'} already given to it in submitted scorecards ${answerCount === 1 ? 'is' : 'are'} deleted with it, and it stops appearing on new scorecards. The rest of each scorecard stays. This can’t be undone.`
        }
        confirmLabel="Delete question"
        onConfirm={handleConfirmDelete}
      />
    </>
  )
}